import { Trans, useTranslation } from 'react-i18next';

import {
  LegalEmailLink,
  LegalIntro,
  LegalPageLayout,
  LegalParagraph,
  LegalSection,
  LegalSections,
} from '../components/legal/LegalPageLayout';

export const PrivacyPage = () => {
  const { t } = useTranslation('legal');

  return (
    <LegalPageLayout title={t('privacy.title')} lastUpdated={t('shared.last_updated')}>
      <LegalIntro>{t('privacy.intro_p1')}</LegalIntro>
      <LegalIntro>{t('privacy.intro_p2')}</LegalIntro>

      <LegalSections>
        <LegalSection title={t('privacy.s1_title')}>
          <LegalParagraph>{t('privacy.s1_p1')}</LegalParagraph>
          <LegalParagraph>
            {t('privacy.s1_p2_prefix')}{' '}
            <LegalEmailLink email="aida@invirtus.co" />
          </LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s2_title')}>
          <LegalParagraph>{t('privacy.s2_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s3_title')}>
          <LegalParagraph>{t('privacy.s3_p1')}</LegalParagraph>
          <LegalParagraph>{t('privacy.s3_p2')}</LegalParagraph>
          <LegalParagraph>{t('privacy.s3_p3')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s4_title')}>
          <LegalParagraph>{t('privacy.s4_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s5_title')}>
          <LegalParagraph>{t('privacy.s5_p1')}</LegalParagraph>
          <LegalParagraph>{t('privacy.s5_p2')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s6_title')}>
          <LegalParagraph>{t('privacy.s6_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s7_title')}>
          <LegalParagraph>{t('privacy.s7_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s8_title')}>
          <LegalParagraph>{t('privacy.s8_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s9_title')}>
          <LegalParagraph>{t('privacy.s9_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s10_title')}>
          <LegalParagraph>{t('privacy.s10_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s11_title')}>
          <LegalParagraph>
            {t('privacy.s11_p1_prefix')}{' '}
            <LegalEmailLink email="aida@invirtus.co" />.
          </LegalParagraph>
          <LegalParagraph>{t('privacy.s11_p2')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s12_title')}>
          <LegalParagraph>
            <Trans ns="legal" i18nKey="privacy.s12_p1" components={{ strong: <strong className="font-medium text-dark-invirtus" /> }} />
          </LegalParagraph>
          <ul className="space-y-2 mb-4">
            <DeclarationItem label={t('privacy.decl_cookie')} reference="N° D-W-763/2026" date="17/04/2026" />
            <DeclarationItem label={t('privacy.decl_newsletter')} reference="N° D-NL-764/2026" date="17/04/2026" />
            <DeclarationItem label={t('privacy.decl_contact')} reference={t('privacy.decl_pending')} />
          </ul>
          <LegalParagraph>
            {t('privacy.s12_p2_prefix')}{' '}
            <LegalEmailLink email="support@invirtus.co" />.
          </LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s13_title')}>
          <LegalParagraph>{t('privacy.s13_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.mailbox_title')}>
          <LegalParagraph>{t('privacy.mailbox_p1')}</LegalParagraph>
          <LegalParagraph>{t('privacy.mailbox_p2')}</LegalParagraph>
          <LegalParagraph>{t('privacy.mailbox_p3')}</LegalParagraph>
          <LegalParagraph>{t('privacy.mailbox_p4')}</LegalParagraph>
          <LegalParagraph>
            {t('privacy.mailbox_limited_use_prefix')}{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noreferrer"
              className="underline hover:no-underline"
            >
              {t('privacy.mailbox_limited_use_link')}
            </a>
            {t('privacy.mailbox_limited_use_suffix')}
          </LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s14_title')}>
          <LegalParagraph>{t('privacy.s14_p1')}</LegalParagraph>
        </LegalSection>

        <LegalSection title={t('privacy.s15_title')}>
          <LegalParagraph>
            {t('privacy.s15_general_prefix')}{' '}
            <LegalEmailLink email="support@invirtus.co" />
          </LegalParagraph>
          <LegalParagraph>
            {t('privacy.s15_privacy_prefix')}{' '}
            <LegalEmailLink email="aida@invirtus.co" />
          </LegalParagraph>
        </LegalSection>
      </LegalSections>

      <div className="mt-14 rounded-2xl border border-dark-invirtus/10 bg-background-gray/60 p-6 sm:p-8">
        <div className="mb-4 font-heading text-[10px] uppercase tracking-[0.3em] text-grey-muted">
          {t('privacy.publisher_label')}
        </div>
        <h2 className="text-dark-invirtus text-xl sm:text-2xl font-semibold mb-5">
          {t('privacy.publisher_title')}
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[14.5px] text-dark-invirtus/80">
          <EditorRow term={t('privacy.editor_company')} definition={t('privacy.editor_company_value')} />
          <EditorRow term={t('privacy.editor_legal_form')} definition={t('privacy.editor_legal_form_value')} />
          <EditorRow term={t('privacy.editor_office')} definition={t('privacy.editor_office_value')} />
          <EditorRow term={t('privacy.editor_rc')} definition={t('privacy.editor_rc_value')} />
          <EditorRow term={t('privacy.editor_jurisdiction')} definition={t('privacy.editor_jurisdiction_value')} />
          <EditorRow term={t('privacy.editor_activity')} definition={t('privacy.editor_activity_value')} />
          <EditorRow term={t('privacy.editor_activity_code')} definition={t('privacy.editor_activity_code_value')} />
          <EditorRow
            term={t('privacy.editor_email')}
            definition={<LegalEmailLink email="support@invirtus.co" />}
          />
          <EditorRow
            term={t('privacy.editor_phone')}
            definition={
              <a href="tel:+212679863481" className="hover:underline">
                +212 6 79 86 34 81
              </a>
            }
          />
        </dl>
      </div>
    </LegalPageLayout>
  );
};

function EditorRow({ term, definition }: { term: string; definition: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="font-heading text-[10px] uppercase tracking-[0.22em] text-grey-muted mb-0.5">
        {term}
      </dt>
      <dd className="text-dark-invirtus">{definition}</dd>
    </div>
  );
}

function DeclarationItem({
  label,
  reference,
  date,
}: {
  label: string;
  reference: string;
  date?: string;
}) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border border-dark-invirtus/10 bg-background-gray/40 px-4 py-3">
      <span className="text-dark-invirtus font-medium">{label}</span>
      <span className="text-grey-muted">·</span>
      <span className="font-heading text-[12px] tracking-[0.04em] text-dark-invirtus/85">
        {reference}
      </span>
      {date && (
        <>
          <span className="text-grey-muted">·</span>
          <span className="font-heading text-[11px] uppercase tracking-[0.22em] text-grey-muted">
            {date}
          </span>
        </>
      )}
    </li>
  );
};
